import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion, isDesktopBreakpoint } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {Function | null} */
  #formChangeHandler = null;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState);
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Reads the current wishlist state from st-wishlist-local (the key the app's
   * wishlist page reads) and applies .is-wishlisted to the modal heart button.
   * @param {Element} modalContent
   */
  #syncWishlistState(modalContent) {
    const btn = /** @type {HTMLElement | null} */ (modalContent.querySelector('.quick-add-wishlist-btn'));
    if (!btn) return;

    const handle = btn.dataset?.handle;
    if (!handle) return;

    let isAdded = false;
    try {
      const stored = localStorage.getItem('st-wishlist-local');
      if (stored) {
        const parsed = JSON.parse(stored);
        isAdded = Array.isArray(parsed) && parsed.some((item) => item?.handle === handle);
      }
    } catch {}

    btn.classList.toggle('is-wishlisted', isAdded);
    btn.setAttribute('aria-label', isAdded ? 'Remove from wishlist' : 'Add to wishlist');
    btn.title = isAdded ? 'Remove from wishlist' : 'Add to wishlist';
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    try {
    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);
    const isFromCache = !!productGrid

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-quick-add-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = productGrid.cloneNode(true);
      await this.updateQuickAddModal(freshContent);
      this.#openQuickAddModal();
      if (!isFromCache) {
        await new Promise(resolve => requestAnimationFrame(resolve));
      } 
    }
  } catch (error) {
    if (error.name === 'AbortError') return;
  }

    
  };

  /**
   * Opens the quick-add modal for an explicit product URL.
   *
   * Used by custom components (e.g. the collection grid slider) that aren't
   * wrapped in a product-card and therefore can't rely on `productPageUrl`.
   * Reuses the same fetch / cache / morph / variant-sync pipeline as `handleClick`.
   *
   * @param {string} productPageUrl - The product URL, including any `?variant=` param.
   */
  openForUrl = async (productPageUrl) => {
    if (!productPageUrl) return;

    let productGrid = this.#cachedContent.get(productPageUrl);

    if (!productGrid) {
      const html = await this.fetchProductPage(productPageUrl);
      if (html) {
        const gridElement = html.querySelector('[data-quick-add-content]');
        if (gridElement) {
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(productPageUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    this.#openQuickAddModal();
  };

  /**
   * Opens the quick-add modal for an explicit product URL.
   *
   * Used by custom components (e.g. the collection grid slider) that aren't
   * wrapped in a product-card and therefore can't rely on `productPageUrl`.
   * Reuses the same fetch / cache / morph / variant-sync pipeline as `handleClick`.
   *
   * @param {string} productPageUrl - The product URL, including any `?variant=` param.
   */
  openForUrl = async (productPageUrl) => {
    if (!productPageUrl) return;

    let productGrid = this.#cachedContent.get(productPageUrl);

    if (!productGrid) {
      const html = await this.fetchProductPage(productPageUrl);
      if (html) {
        const gridElement = html.querySelector('[data-quick-add-content]');
        if (gridElement) {
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(productPageUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
 async fetchProductPage(productPageUrl) {
  if (!productPageUrl) return null;

  this.#abortController?.abort();
  this.#abortController = new AbortController();

  try {
    const url = new URL(productPageUrl, window.location.origin);
    url.searchParams.set('section_id', 'section-rendering-quick-add');

    const response = await fetch(url.toString(), {
      signal: this.#abortController.signal,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
    }

    const responseText = await response.text();
    const html = new DOMParser().parseFromString(responseText, 'text/html');

    return html;
  } catch (error) {
    if (error.name === 'AbortError') {
      return null;
    } else {
      throw error;
    }
  } finally {
    this.#abortController = null;
  }
}

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    

    if (!productGrid || !modalContent) return;

    modalContent.replaceChildren(productGrid.cloneNode(true))
    
    await new Promise(resolve => requestAnimationFrame(resolve));

    this.#syncWishlistState(modalContent);
    this.#loadQuickAddRecommendations(modalContent)
    this.#syncVariantSelection(modalContent);
    this.#setupVariantChangeHandler(modalContent);

    modalContent.dispatchEvent(new CustomEvent('quick-add-updated', {
      bubbles: true,
      detail: {modalContent}
    }))
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }

  #setupVariantChangeHandler(modalContent) {
    const wrapper = modalContent.querySelector('[data-variant-picker-wrapper]');
    if (!wrapper) return;
    const form = wrapper.querySelector('form');
    if (!form) return;

    if (this.#formChangeHandler && form) {
      form.removeEventListener('change', this.#formChangeHandler);
    }

    this.#formChangeHandler = () => {
        const checkedInputs = form.querySelectorAll('input[type="radio"]:checked');
        const selectedValues = Array.from(checkedInputs).map(input => input.value);
        const jsonScript = wrapper.querySelector('script[type="application/json"][data-variant-data]');
        if (!jsonScript) return;
        let variants;
        try {
            variants = JSON.parse(jsonScript.textContent);
        } catch {
            return;
        }
        const match = variants.find(v =>
            v.options.length === selectedValues.length &&
            v.options.every((opt, i) => opt === selectedValues[i])
        );
        if (!match) return;
        
        checkedInputs.forEach(input => {
          const fieldset = input.closest('fieldset');
          if(fieldset) {
            const valueSpan = fieldset.querySelector('.quick-add-fieldset__value');
            if(valueSpan) {
              valueSpan.textContent = input.value;
            }
          }
        })
        const firstChecked = checkedInputs[0];
        const optionValueId = firstChecked?.dataset.optionValueId || '';
        wrapper.dispatchEvent(new VariantSelectedEvent({
            id: optionValueId,
        }));
        wrapper.dispatchEvent(new VariantUpdateEvent(match, optionValueId, {
            html: document.createDocumentFragment(),
            productId: wrapper.dataset.productId || '',
            newProduct: undefined,
        }));
    };
    form.addEventListener('change', this.#formChangeHandler);
    this.#formChangeHandler();
}

 async #loadQuickAddRecommendations(modalContent) {
    const container = modalContent.querySelector('.quick-add-recommendations');
    if (!container) return;

    const productId = container.dataset.productId;
    const moneyFormat = container.dataset.moneyFormat || '${{amount}}';
    const currency = window.Shopify?.currency?.active || 'USD';
    if (!productId) return;

    try {
      const url = `/recommendations/products.json?limit=3&product_id=${productId}`;
      const response = await fetch(url);
      if (!response.ok) return;

      const { products } = await response.json();
      if (!products || !products.length) {
        container.remove();
        return;
      }

      const grid = container.querySelector('.quick-add-recommendations__grid');
      grid.innerHTML = '';

      products.slice(0, 3).forEach(product => {
        const imageUrl = product.featured_image
          ? (typeof product.featured_image === 'object' ? product.featured_image.src : product.featured_image)
          : '';
        const imageSrc = imageUrl ? `${imageUrl}&width=300` : '';

        const price = formatMoney(product.price, moneyFormat, currency);

        const card = document.createElement('a');
        card.className = 'quick-add-recs__link';
        card.href = `/products/${product.handle}`;

        card.innerHTML = `
          <img src="${imageSrc}" alt="${product.title.replace(/"/g, '"')}" class="quick-add-recs__image" loading="lazy" width="200" height="200">
          <div class="quick-add-recs__title">${product.title}</div>
          <div class="quick-add-recs__price">${price}</div>
        `;
        grid.appendChild(card);
      });
   } catch (e) {
      console.error('[quick-add] Failed to load recommendations:', e);
      container.remove();
    }
  }
}


if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal: this.#abortController.signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener('click', this.#handleWishlistClick, { signal: this.#abortController.signal });

  }

  #handleVariantUpdate = (event) => {
  if (!event.target || !this.contains(event.target)) return;

  const img = document.getElementById('quick-add-image');
  if (!img) return;

  const preview = event.detail?.resource?.featured_media?.preview_image;
  const src = preview?.src;
  if (src) {
    const width = Math.min(preview.width || 500, 500);
    const height = preview.width && preview.height ? Math.round((preview.height * width) / preview.width) : width;

    img.src = `${src}&width=${width}`;
    img.srcset = '';
    img.width = width;
    img.height = height;
  }
  };

  #handleWishlistClick = (event) => {
    const btn = event.target?.closest?.('.quick-add-wishlist-btn');
    if (!btn) return;
    const isAdded = btn.classList.toggle('is-wishlisted');
    const label = isAdded ? 'Remove from wishlist' : 'Add to wishlist';
    btn.setAttribute('aria-label', label);
    btn.title = label;
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
